import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { ChangeEventHandler, FunctionComponent, KeyboardEvent, MouseEventHandler, SetStateAction } from 'react';
import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { SkillShort } from '~/shared/data-contract/skill';
import { IconCrossSmall, IconSearch } from '~/ui/core/icons';
import { SkillBadge } from '~/ui/domain/skill/SkillBadge';
import { theme } from '~/ui/theme/cobrainer-theme';

import { useNavigationToEntity } from '../common/use-navigation-to-entity';
import { useManySkillsQuery } from '../query-hooks/skills';
import type { DropdownData } from './DropDown';

type GlobalSearchInputV2Props = {
    readonly currentCursor: number;
    readonly dropDownData: readonly DropdownData[];
    readonly getActiveDropdownElement: () => DropdownData | undefined;
    readonly handleGlobalSearch: () => void;
    readonly onCloseDropdown: () => void;
    readonly onOpenDropdown: () => void;
    readonly searchTerm: string;
    readonly setCurrentCursor: (value: SetStateAction<number>) => void;
    readonly setSearchTerm: (value: SetStateAction<string>) => void;
    readonly setSkillIds: (value: SetStateAction<readonly string[]>) => void;
    readonly skillIds: readonly string[];
};

type GlobalSearchSkillChipProps = {
    readonly setSkillIds: (value: SetStateAction<readonly string[]>) => void;
    readonly skillIds: readonly string[];
    readonly skills: readonly SkillShort[];
};

const SkillChipsContainer = styled.div(({ theme }) => ({
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    justifyContent: 'flex-start',
    paddingRight: theme.spacing.m,
    // flexWrap: 'wrap',
}));

const GlobalSearchSkillChip: FunctionComponent<GlobalSearchSkillChipProps> = ({ setSkillIds, skillIds, skills }) => {
    const { actions } = useContext(ApplicationContext);

    const handleDelete = (skillId: string): void => {
        const updatedSkillIds = skillIds.filter((id) => id !== skillId);

        setSkillIds(updatedSkillIds);
    };

    return (
        <SkillChipsContainer aria-label="Global Search Skill Chips">
            {skills.map((skill) => {
                return (
                    <SkillBadge
                        key={skill.id}
                        actions={
                            <IconCrossSmall
                                color={theme.colors.gray[700]}
                                size={16}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(skill.id);
                                }}
                            />
                        }
                        skill={{ title: skill.title, type: skill.skillType }}
                        style={{
                            borderRadius: theme.borderRadius.s,
                            fontSize: '0.8rem',
                            paddingBottom: theme.spacing.xxs,
                            paddingTop: theme.spacing.xxs,
                        }}
                        onClick={() => actions.openSkillDetails(skill.id)}
                    />
                );
            })}
        </SkillChipsContainer>
    );
};

type ExactSkillsSectionProps = {
    readonly setSkillIds: (value: SetStateAction<readonly string[]>) => void;
    readonly skillIds: readonly string[];
};

const ExactSkillsSection: FunctionComponent<ExactSkillsSectionProps> = ({ setSkillIds, skillIds }) => {
    const { data } = useManySkillsQuery({ enabled: is.nonEmptyArray(skillIds), skillIds });

    if (is.undefined(data) || is.emptyArray(data)) {
        return <span />;
    }

    return <GlobalSearchSkillChip setSkillIds={setSkillIds} skillIds={skillIds} skills={data} />;
};

const SearchInputContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.colors.gray[300],
    borderRadius: theme.borderRadius.l,
    display: 'flex',
    justifyContent: 'space-between',
    padding: `${theme.spacing.m} ${theme.spacing.l}`,
}));

const InputWrapper = styled.div({
    display: 'grid',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    gridTemplateColumns: 'auto minmax(300px, 1fr)',
});

const StyledInput = styled.input(({ theme }) => ({
    '&::placeholder': {
        color: theme.colors.gray[700],
    },
    backgroundColor: theme.colors.gray[300],
    border: 'none',
    color: theme.colors.gray[800],
    ...theme.typography.body.S.regular,
    outline: 'none',
    padding: 0,
    width: '100%',
}));

const SearchButton = styled.button(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.gray[900],
    cursor: 'pointer',
    display: 'flex',
    height: '24px',
    justifyContent: 'center',
    padding: 0,
    width: '24px',
}));

export const GlobalSearchInputV2: FunctionComponent<GlobalSearchInputV2Props> = ({
    dropDownData,
    getActiveDropdownElement,
    handleGlobalSearch,
    onCloseDropdown,
    onOpenDropdown,
    searchTerm,
    setCurrentCursor,
    setSearchTerm,
    setSkillIds,
    skillIds,
}) => {
    const { navigateToJobPage, navigateToLearningOpportunityPage, navigateToUserProfile } = useNavigationToEntity();
    const { translate } = useContext(ApplicationContext);

    const onAddSkill = (skillId: string): void => {
        setSkillIds([...skillIds, skillId]);
    };

    const onDeleteLastSkill = (): void => {
        const updatedSkillIds = skillIds.slice(0, -1);
        setSkillIds(updatedSkillIds);
    };

    const changeSearchTerm: ChangeEventHandler<HTMLInputElement> = (event): void => {
        const searchValue = event.target.value;
        onOpenDropdown();
        setSearchTerm(searchValue);
    };

    const handleClick: MouseEventHandler = (event) => {
        event.stopPropagation();
        handleGlobalSearch();
    };

    const handleEnterKey = (): void => {
        const active = getActiveDropdownElement();

        if (is.undefined(active)) {
            handleGlobalSearch();

            return;
        }

        const { id, type } = active;
        if (type === 'job') {
            navigateToJobPage(id);
        }
        if (type === 'skill') {
            onAddSkill(id);
        }
        if (type === 'user') {
            navigateToUserProfile(id);
        }
        if (type === 'learning-opportunity') {
            navigateToLearningOpportunityPage(id);
        }

        onCloseDropdown();
        setSearchTerm('');
        setCurrentCursor(-1);
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
        e.stopPropagation();

        switch (e.key) {
            case 'Enter':
                handleEnterKey();
                break;
            case 'ArrowUp':
                setCurrentCursor((prevState) => (prevState > 0 ? prevState - 1 : -1));
                break;
            case 'ArrowDown':
                setCurrentCursor((prevState) => (prevState === dropDownData.length - 1 ? 0 : prevState + 1));
                break;
            case 'Backspace':
                if (is.emptyString(searchTerm)) {
                    onDeleteLastSkill();
                }
                break;
            default:
                break;
        }
    };

    return (
        <SearchInputContainer aria-label="Global Search Input">
            <InputWrapper>
                <ExactSkillsSection setSkillIds={setSkillIds} skillIds={skillIds} />
                <StyledInput
                    aria-label="Global Search Input Field"
                    autoComplete="off"
                    id="global-search-input"
                    placeholder={translate('pages.global-search.placeholder')}
                    type="text"
                    value={searchTerm}
                    onChange={changeSearchTerm}
                    onClick={onOpenDropdown}
                    onKeyDown={handleKeyPress}
                />
            </InputWrapper>
            <SearchButton id="global-search-button" type="button" onClick={handleClick}>
                <IconSearch size={16} />
            </SearchButton>
        </SearchInputContainer>
    );
};
